import { RoutePlanProposalDTO, RoutePlanStepEnum } from 'api/model';
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { traceForPackage } from './selection-helpers';

const STEP_LABELS: Record<RoutePlanStepEnum, string> = {
  [RoutePlanStepEnum.Pool]: 'Pool',
  [RoutePlanStepEnum.Chains]: 'Kedjor',
  [RoutePlanStepEnum.Assignment]: 'Tilldelning',
  [RoutePlanStepEnum.DriverSwaps]: 'Skiften',
  [RoutePlanStepEnum.Gaps]: 'Luckor',
  [RoutePlanStepEnum.ReturnLegRepair]: 'Hemresepass',
  [RoutePlanStepEnum.Classification]: 'Klassning',
  [RoutePlanStepEnum.ShortDayMerge]: 'Korta heltidsdagar',
  [RoutePlanStepEnum.IdleWaitRepair]: 'Väntepass',
  [RoutePlanStepEnum.CustomerRebooking]: 'Ny tid med kund',
  [RoutePlanStepEnum.IdleWaitRepair]: 'Håltimmar',
  [RoutePlanStepEnum.FreedFieldTesterSwap]: 'Frigjorda förare',
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px 16px;
  background: ${({ theme }) => theme.colors.background.secondary};
  border-radius: 4px;
`;

const Header = styled.div`
  display: flex;
  align-items: baseline;
  gap: 12px;
`;

const Title = styled.h3`
  margin: 0;
`;

const Subtitle = styled.span`
  font-size: 0.85em;
  color: rgb(120, 120, 120);
`;

const Steps = styled.div`
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding-bottom: 4px;
`;

const Step = styled.div<{ highlight?: boolean }>`
  flex: 1 0 150px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 10px;
  border-radius: 4px;
  border: 1px solid
    ${({ highlight }) => (highlight ? 'rgb(30, 90, 160)' : 'rgb(210, 210, 210)')};
  background: ${({ theme }) => theme.colors.background.primary};
`;

const StepName = styled.span`
  font-size: 0.72em;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: rgb(120, 120, 120);
`;

const StepValue = styled.span`
  font-size: 1.25em;
  font-weight: 700;
`;

const StepNote = styled.span`
  font-size: 0.78em;
  color: rgb(90, 90, 90);
`;

const PackagePicker = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  font-size: 0.85em;
`;

const PackageButton = styled.button<{ active?: boolean }>`
  border: 1px solid ${({ active }) => (active ? 'rgb(30, 90, 160)' : 'rgb(200, 200, 200)')};
  background: ${({ active }) => (active ? 'rgb(30, 90, 160)' : 'transparent')};
  color: ${({ active }) => (active ? '#fff' : 'inherit')};
  border-radius: 3px;
  padding: 2px 8px;
  font: inherit;
  font-size: 0.95em;
  cursor: pointer;
`;

const Trace = styled.table`
  border-collapse: collapse;
  width: 100%;

  td {
    padding: 5px 8px;
    border-bottom: 1px solid #b5b3b35c;
    vertical-align: top;
    font-size: 0.88em;
  }

  td:first-child {
    width: 9rem;
    white-space: nowrap;
    color: rgb(120, 120, 120);
  }

  td:nth-child(2) {
    width: 10rem;
    white-space: nowrap;
    font-weight: 600;
  }
`;

const TraceEmpty = styled.div`
  font-size: 0.88em;
  color: rgb(120, 120, 120);
`;

interface Props {
  proposal: RoutePlanProposalDTO;
}

/**
 * "Så togs förslaget fram" — fönstret in i körningen.
 *
 * Hemresepasset arbetar under ytan: paketkorten visar bara resultatet, aldrig att ett ben flyttats
 * dit från ett annat paket. Utan den här vyn finns ingen väg tillbaka till varför förslaget ser ut
 * som det gör, och ett förslag man inte kan ifrågasätta är ett förslag man slutar lita på.
 *
 * Stegraden svarar på "vart tog uppdragen vägen?", spåret på "varför fick just det här paketet
 * just den här lösningen?". Ingen ny hämtning — allt läses ur förslaget som redan finns.
 */
const RunOverview: React.FC<Props> = ({ proposal }) => {
  const [selectedPackage, setSelectedPackage] = useState<number | null>(
    proposal.packages[0]?.packageIndex ?? null
  );

  const trace = useMemo(
    () =>
      selectedPackage === null ? [] : traceForPackage(proposal.trace, selectedPackage),
    [proposal.trace, selectedPackage]
  );

  return (
    <Wrapper>
      <Header>
        <Title>Så togs förslaget fram</Title>
        <Subtitle>
          Samma körning som förhandsvisningen. Varje steg visar vad som kom in, vad som gick
          vidare och vad som föll bort.
        </Subtitle>
      </Header>

      <Steps>
        {proposal.steps.map((step) => (
          <Step
            key={step.step}
            highlight={step.step === RoutePlanStepEnum.ReturnLegRepair && step.out > 0}
          >
            <StepName>{STEP_LABELS[step.step] ?? `Steg ${step.step}`}</StepName>
            <StepValue>
              {step.out}
              {step.in !== step.out && <> / {step.in}</>}
            </StepValue>
            <StepNote>
              {step.dropped > 0 ? `${step.dropped} föll bort` : 'inget bortfall'}
            </StepNote>
            {step.note && <StepNote>{step.note}</StepNote>}
          </Step>
        ))}
      </Steps>

      {proposal.packages.length > 0 && (
        <>
          <PackagePicker>
            <span>Spår för paket:</span>
            {proposal.packages.map((pkg) => (
              <PackageButton
                active={pkg.packageIndex === selectedPackage}
                key={pkg.packageIndex}
                onClick={() => setSelectedPackage(pkg.packageIndex)}
                type="button"
              >
                {pkg.packageIndex}
                {pkg.legs[0]?.registrationNumber
                  ? ` · ${pkg.legs[0].registrationNumber}`
                  : ''}
              </PackageButton>
            ))}
          </PackagePicker>

          {trace.length === 0 ? (
            <TraceEmpty>Inget spår skrivet för det här paketet.</TraceEmpty>
          ) : (
            <Trace>
              <tbody>
                {trace.map((entry, index) => (
                  // Ett steg kan skriva flera rader för samma paket (t.ex. både en prövad och en
                  // vald kandidat), så indexet ingår i nyckeln.
                  // eslint-disable-next-line react/no-array-index-key
                  <tr key={`${entry.step}-${index}`}>
                    <td>
                      {entry.step} · {STEP_LABELS[entry.step] ?? entry.step}
                    </td>
                    <td>{entry.headline}</td>
                    <td>{entry.detail}</td>
                  </tr>
                ))}
              </tbody>
            </Trace>
          )}
        </>
      )}
    </Wrapper>
  );
};

export default RunOverview;
